import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Spinner } from '@trello/nachos/spinner';
import { RouterLink } from '@trello/router/router-link';

import { Alerts } from 'app/scripts/views/lib/Alerts';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { InputErrors } from './InputErrors';
import { usePluginPrivacyAndComplianceQuery } from './PluginPrivacyAndComplianceQuery.generated';
import { isEmptyOr, isValidHttpUrl } from './pluginValidators';
import { useUpdatePluginPrivacyAndComplianceMutation } from './UpdatePluginPrivacyAndComplianceMutation.generated';
import { useSyncedState } from './useSyncedState';
import { useValidatedSyncedState } from './useValidatedState';

import styles from './PowerUpPrivacyAndCompliancePage.less';

const format = forTemplate('app_management');

export const PowerUpPrivacyAndCompliancePage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, loading } = usePluginPrivacyAndComplianceQuery({
    variables: {
      idPlugin,
    },
    // Privacy and compliance doesn't update apiKey field after its been generated from API key page
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'network-only',
    waitOn: ['None'],
  });

  const [updatePluginPrivacyAndCompliance, { loading: updating }] =
    useUpdatePluginPrivacyAndComplianceMutation();

  const [privacyUrl, setPrivacyUrl, privacyUrlErrors] = useValidatedSyncedState(
    data?.plugin?.privacyUrl ? data.plugin.privacyUrl : '',
    '',
    isEmptyOr(isValidHttpUrl(format('error-invalid-url'))),
  );

  const [storesPersonalData, setStoresPersonalData] = useSyncedState(
    data?.plugin?.compliance ? data.plugin.compliance.storesPersonalData : null,
    null,
  );

  const [apiKey, setApiKey, apiKeyErrors] = useValidatedSyncedState(
    data?.plugin?.apiKey ? data.plugin.apiKey : undefined,
    undefined,
    (value: string | undefined) => {
      if (storesPersonalData && !value) {
        return format('error-api-key-is-required');
      }

      if (value && !/^[a-f0-9]{32}$/i.test(value)) {
        return format('error-invalid-api-key');
      }

      return null;
    },
  );

  const updatePlugin = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-plugin/privacyAndCompliance',
      source: 'powerUpAdminPrivacyAndComplianceScreen',
      attributes: {
        idPlugin,
      },
    });
    try {
      await updatePluginPrivacyAndCompliance({
        variables: {
          idPlugin,
          privacyUrl,
          apiKey,
          storesPersonalData,
        },
      });
      Analytics.taskSucceeded({
        taskName: 'edit-plugin/privacyAndCompliance',
        traceId,
        source: 'powerUpAdminPrivacyAndComplianceScreen',
        attributes: {
          idPlugin,
        },
      });
      Alerts.show('changes-saved', 'info', 'changessaved', 3000);
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-plugin/privacyAndCompliance',
        traceId,
        source: 'powerUpAdminPrivacyAndComplianceScreen',
        attributes: {
          idPlugin,
        },
        error,
      });
    }
  }, [
    updatePluginPrivacyAndCompliance,
    idPlugin,
    privacyUrl,
    apiKey,
    storesPersonalData,
  ]);

  if (loading) {
    return <LoadingSpinner />;
  }

  const canUpdate = !updating && !privacyUrlErrors && !apiKeyErrors;

  return (
    <>
      <div className={styles.header}>
        <h1>{format('privacy-and-compliance')}</h1>
      </div>
      <div className={styles.formSection}>
        <label>
          {format('privacy-url')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': privacyUrlErrors,
            })}
            name="privacy-url"
            value={privacyUrl}
            onChange={(e) => setPrivacyUrl(e.target.value)}
            disabled={updating}
          />
          <InputErrors errors={privacyUrlErrors} />
        </label>
        <div
          className={styles.inputDescription}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- FIXME
          dangerouslySetInnerHTML={{
            __html: format('privacy-url-description'),
          }}
        />
      </div>
      <div className={styles.formSection}>
        <div className={styles.storesPersonalDataContainer}>
          <fieldset>
            <legend>{format('stores-personal-data')}</legend>
            <label>
              <input
                className={styles.storesPersonalDataRadio}
                type="radio"
                name="stores-personal-data-true"
                onChange={(e) => setStoresPersonalData(e.target.checked)}
                checked={storesPersonalData === true}
                aria-checked={storesPersonalData === true}
              />
              {format('yes')}
            </label>
            <label>
              <input
                className={styles.storesPersonalDataRadio}
                type="radio"
                name="stores-personal-data-false"
                onChange={(e) => setStoresPersonalData(!e.target.checked)}
                checked={storesPersonalData === false}
                aria-checked={storesPersonalData === false}
              />
              {format('no')}
            </label>
          </fieldset>
        </div>
        <div className={styles.inputDescription}>
          <p>{format('stores-personal-data-description')}</p>
          <p
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- FIXME
            dangerouslySetInnerHTML={{
              __html: format('stores-personal-data-information'),
            }}
          />
        </div>
      </div>
      <div className={styles.formSection}>
        {apiKey ? (
          <label className={styles.apiKey}>
            {format('api-key')}
            <input
              type="text"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error': apiKeyErrors,
              })}
              name="api-key"
              placeholder={format('api-key')}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={updating}
              readOnly={true}
            />
            <InputErrors errors={apiKeyErrors} />
          </label>
        ) : (
          <div className={styles.apiKey}>
            <RouterLink href={`/power-ups/${idPlugin}/edit/api-key`}>
              <Button appearance="primary" className={styles.registerBtn}>
                {format('register-api-key')}
              </Button>
            </RouterLink>
            <span className={styles.isInvalidApiKey}>{apiKeyErrors}</span>
          </div>
        )}
        <div className={styles.inputDescription}>
          <p>{format('api-key-description')}</p>
          <p>{format('api-key-information')}</p>
          <p>
            <RouterLink href={`/power-ups/${idPlugin}/edit/api-key`}>
              {format('manage-your-api-key')}
            </RouterLink>
          </p>
        </div>
      </div>
      <div className={classNames(styles.footer, styles.formFooter)}>
        <Button
          appearance="primary"
          onClick={updatePlugin}
          isDisabled={!canUpdate}
        >
          {updating ? <Spinner small /> : format('save')}
        </Button>
      </div>
    </>
  );
};
